<script setup lang="ts">
/**
 * Champ de montant : affiche la valeur formatée comme un champ, et ouvre le pavé numérique
 * sur mesure dans un sheet. Pas de clavier système, pas de saisie décimale.
 */
import { ref, useId, watch } from 'vue'

import { fr } from '@/copy/fr'
import { formatMoney } from '@/domain'

import AmountInput from './AmountInput.vue'
import Button from './Button.vue'
import Field from './Field.vue'
import Sheet from './Sheet.vue'

const props = withDefaults(
  defineProps<{
    label: string
    hint?: string
    error?: string
    placeholder?: string
    max?: number
    compact?: boolean
  }>(),
  { hint: undefined, error: undefined, placeholder: undefined, max: undefined, compact: false },
)

const model = defineModel<number | null>({ default: null })
const id = useId()
const open = ref(false)
const draft = ref<number | null>(null)
/** Nouvelle saisie à chaque ouverture : le montant présent est proposé « sélectionné ». */
const session = ref(0)

watch(open, (isOpen) => {
  if (!isOpen) return
  draft.value = model.value
  session.value += 1
})

function confirm() {
  model.value = draft.value
  open.value = false
}
</script>

<template>
  <Field v-if="!compact" :id="id" :label="label" :hint="hint" :error="error">
    <template #default="{ describedBy }">
      <button
        :id="id"
        type="button"
        aria-haspopup="dialog"
        :aria-expanded="open"
        :aria-describedby="describedBy"
        :aria-invalid="error ? 'true' : undefined"
        :class="[
          'flex h-control w-full items-center justify-between gap-3 rounded-control bg-surface px-4 text-left transition-colors duration-80 active:bg-surface-strong',
          error ? 'outline-2 outline-offset-2 outline-danger' : '',
        ]"
        @click="open = true"
      >
        <span :class="['text-body tabular-nums', model === null ? 'text-ink-muted' : 'text-ink']">
          {{ model === null ? (placeholder ?? formatMoney(0)) : formatMoney(model) }}
        </span>
      </button>
    </template>
  </Field>
  <button
    v-else
    type="button"
    :aria-label="label"
    aria-haspopup="dialog"
    :class="[
      'flex h-control-compact min-w-28 items-center justify-end rounded-control bg-surface px-3 text-body tabular-nums transition-colors duration-80 active:bg-surface-strong',
      model === null ? 'text-ink-muted' : 'text-ink',
    ]"
    @click="open = true"
  >
    {{ model === null ? (placeholder ?? formatMoney(0)) : formatMoney(model) }}
  </button>

  <Sheet v-model:open="open" :title="props.label" hide-title>
    <div class="pt-2">
      <AmountInput :key="session" v-model="draft" :label="props.label" :max="max" />
    </div>
    <template #footer>
      <Button block @click="confirm">{{ fr.ui.validate }}</Button>
    </template>
  </Sheet>
</template>
